import type { CsvTable } from './csv';
import { rowValues } from './csv';
import type { ImportIssue, ImportPlan, PlannedRow } from './types';
import { countActions, error, warning } from './types';

/** Die Spalten des C.O.S.-Arista-Maschinenexports (gemessen an Maschinendaten.CSV, 2026-07-17). */
export const aristaMachineColumns = [
  'ERFASS_NR', 'LIEFERANT', 'KAUFDATUM', 'FABRIKAT', 'GERATE_ART', 'GERATE_TYP', 'ALT_NAME',
] as const;

const requiredColumns = ['ERFASS_NR', 'FABRIKAT'] as const;

export interface MachineDraft {
  /** Die Arista-Erfassungsnummer. Wird ÜBERNOMMEN, nicht neu vergeben. */
  readonly number: string;
  readonly typeId: string;
  /** null heißt bewusst "keinem Kunden zugeordnet" -- nie geraten. */
  readonly customerId: string | null;
  readonly manufacturer: string;
  readonly model: string;
  /** Kaufdatum als ISO-Datum -- in FORQ der Bestandszugang. */
  readonly activeSince: string | null;
  readonly notes: string | null;
}

export interface ExistingMachine {
  readonly number: string;
}

export interface MachineTypeOption {
  readonly id: string;
  readonly name: string;
}

/** Ein Kunde, wie er für die Namenszuordnung gebraucht wird. */
export interface CustomerLookupEntry {
  readonly id: string;
  readonly number: string;
  readonly name: string;
}

export interface MachineImportOptions {
  /** Maschinentypen des Mandanten -- GERATE_ART wird darauf abgebildet. */
  readonly types: readonly MachineTypeOption[];
  /**
   * GERATE_ART (wörtlich, wie in der Datei) -> typeId. Kommt vom Anwender.
   * Im echten Export stehen über 100 verschiedene Werte für 2232 Maschinen: Tippfehler
   * ("Elekto Gabelstapler", "Hochhubwagan"), Status im Typfeld ("Gabelstapler / Schrott"),
   * Seriennummern ("Hubwagen 102PM2582") und Dinge, die keine Maschine sind ("Batterie 24V").
   * Die häufigsten 10 decken 86 % ab -- den Rest entscheidet ein Mensch, nicht eine Heuristik.
   */
  readonly typeMapping?: Readonly<Record<string, string>>;
  /** Für die Kundenzuordnung über ALT_NAME. */
  readonly customers?: readonly CustomerLookupEntry[];
  /**
   * ERFASS_NR -> customerId, von Hand entschieden. Gewinnt immer gegen die Namenssuche.
   * Genau hierfür ist die Liste da: bei 249 Maschinen ist der Name mehrdeutig.
   */
  readonly customerOverrides?: Readonly<Record<string, string>>;
  readonly existing?: readonly ExistingMachine[];
}

/**
 * Namen vergleichbar machen: Groß/klein, Rechtsform und Zeichensetzung raus.
 * Bewusst KEIN Fuzzy-Matching (Levenshtein o. ä.) -- "Meier GmbH" und "Meier GmbH & Co. KG" sind
 * ähnlich UND verschiedene Firmen. Wer hier rät, hängt Maschinen an den falschen Kunden.
 */
export function normalizeCompanyName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[.,&/+-]/g, ' ')
    .replace(/\b(gmbh|mbh|co|kg|ag|ohg|gbr|e\s?v|ug|se|ltd|bv|b\s?v|sa|s\s?r\s?o|oy)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type CustomerMatchKind =
  /** Genau ein Kunde -- sicher. */
  | 'unique'
  /** Mehrere Kunden heißen so. Wird NICHT zugeordnet. */
  | 'ambiguous'
  /** Kein Kunde gefunden. */
  | 'none'
  /** ALT_NAME war leer. */
  | 'empty';

export interface CustomerMatch {
  readonly kind: CustomerMatchKind;
  readonly customerId: string | null;
  /** Bei 'ambiguous': die Kandidaten, damit ein Mensch wählen kann. */
  readonly candidates: readonly CustomerLookupEntry[];
}

/**
 * Ordnet eine Maschine über ALT_NAME einem Kunden zu -- der Notbehelf, solange K_NUMMER im
 * Maschinenexport fehlt.
 *
 * Gemessen am echten Export (2232 Maschinen): 79,5 % eindeutig, 11,2 % MEHRDEUTIG, 5,5 % ohne
 * ALT_NAME, 2,1 % ohne Treffer. Die Mehrdeutigkeit ist echt und nicht wegzurechnen: es gibt
 * 55 Kunden namens "Prosol Lacke + Farben GmbH" -- Filialen in Kiel, Fulda, Wiesbaden, Mannheim …
 * ALT_NAME kann unmöglich sagen, welche gemeint ist. Deshalb wird bei 'ambiguous' NICHTS
 * zugeordnet, statt die erstbeste Filiale zu nehmen.
 */
export function matchCustomerByName(
  altName: string,
  index: ReadonlyMap<string, readonly CustomerLookupEntry[]>,
): CustomerMatch {
  const cleaned = altName.trim();
  if (!cleaned) return { kind: 'empty', customerId: null, candidates: [] };

  const hits = index.get(normalizeCompanyName(cleaned));
  if (!hits || hits.length === 0) return { kind: 'none', customerId: null, candidates: [] };
  if (hits.length === 1) return { kind: 'unique', customerId: hits[0].id, candidates: hits };
  return { kind: 'ambiguous', customerId: null, candidates: hits };
}

export function buildCustomerIndex(
  customers: readonly CustomerLookupEntry[],
): ReadonlyMap<string, readonly CustomerLookupEntry[]> {
  const index = new Map<string, CustomerLookupEntry[]>();
  for (const customer of customers) {
    const key = normalizeCompanyName(customer.name);
    if (!key) continue;
    const bucket = index.get(key);
    if (bucket) bucket.push(customer);
    else index.set(key, [customer]);
  }
  return index;
}

/** Arista schreibt YYYYMMDD. Ungültiges wird gemeldet, nicht stillschweigend zu "heute". */
export function parsePurchaseDate(value: string): { readonly iso: string | null; readonly problem: string | null } {
  const raw = value.trim();
  if (!raw) return { iso: null, problem: null };
  if (!/^\d{8}$/.test(raw)) return { iso: null, problem: `"${raw}" ist kein Datum im Format JJJJMMTT.` };

  const year = Number(raw.slice(0, 4));
  const month = Number(raw.slice(4, 6));
  const day = Number(raw.slice(6, 8));
  const date = new Date(Date.UTC(year, month - 1, day));
  // Der Round-Trip fängt den 31.02. ab: Date rollt still auf den 03.03. weiter.
  const valid = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  if (!valid) return { iso: null, problem: `"${raw}" gibt es als Datum nicht.` };
  return { iso: date.toISOString().slice(0, 10), problem: null };
}

function clean(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '-') return null;
  return trimmed;
}

export function planMachineImport(
  table: CsvTable,
  options: MachineImportOptions,
): ImportPlan<MachineDraft> {
  const existingNumbers = new Set((options.existing ?? []).map((entry) => entry.number));
  const knownTypeIds = new Set(options.types.map((type) => type.id));
  const customerIndex = buildCustomerIndex(options.customers ?? []);
  const customerById = new Map((options.customers ?? []).map((entry) => [entry.id, entry]));

  const missingColumns = requiredColumns.filter((column) => !table.header.includes(column));
  const unusedColumns = table.header.filter(
    (column) => !(aristaMachineColumns as readonly string[]).includes(column),
  );

  const seenNumbers = new Map<string, number>();
  const rows: PlannedRow<MachineDraft>[] = [];

  for (const row of table.rows) {
    const values = rowValues(table.header, row);
    const issues: ImportIssue[] = [];

    const number = clean(values.ERFASS_NR ?? '') ?? '';
    const manufacturer = clean(values.FABRIKAT ?? '');
    const model = clean(values.GERATE_TYP ?? '');
    const rawType = (values.GERATE_ART ?? '').trim();

    if (!number) issues.push(error('ERFASS_NR', 'Ohne Erfassungsnummer kann die Zeile nicht zugeordnet werden.'));
    if (!manufacturer) issues.push(error('FABRIKAT', 'Ohne Fabrikat kann keine Maschine angelegt werden.'));
    if (!model) issues.push(warning('GERATE_TYP', 'Kein Typ angegeben.'));

    const firstSeen = seenNumbers.get(number);
    if (number && firstSeen !== undefined) {
      issues.push(error('ERFASS_NR', `Erfassungsnummer ${number} steht schon in Zeile ${firstSeen}.`));
    } else if (number) {
      seenNumbers.set(number, row.line);
    }

    // Maschinentyp: PFLICHT in FORQ. Ohne Zuordnung wird die Zeile blockiert -- ein Fantasietyp
    // wäre schlimmer als eine Zeile, die der Anwender bewusst zuordnet.
    const typeId = options.typeMapping?.[rawType];
    if (!rawType) {
      issues.push(error('GERATE_ART', 'Keine Geräteart angegeben – bitte einen Maschinentyp zuordnen.'));
    } else if (!typeId) {
      issues.push(error('GERATE_ART', `Geräteart "${rawType}" ist keinem FORQ-Maschinentyp zugeordnet.`));
    } else if (!knownTypeIds.has(typeId)) {
      issues.push(error('GERATE_ART', `Der zugeordnete Maschinentyp existiert nicht (mehr).`));
    }

    const purchase = parsePurchaseDate(values.KAUFDATUM ?? '');
    if (purchase.problem) issues.push(warning('KAUFDATUM', `${purchase.problem} Wird ohne Zugangsdatum angelegt.`));

    // Kundenzuordnung: Handentscheidung schlägt Namenssuche.
    const altName = (values.ALT_NAME ?? '').trim();
    const override = number ? options.customerOverrides?.[number] : undefined;
    const match = matchCustomerByName(altName, customerIndex);
    let customerId: string | null = null;

    if (override) {
      if (customerById.has(override)) customerId = override;
      else issues.push(error('ALT_NAME', 'Der von Hand gewählte Kunde existiert nicht (mehr).'));
    } else if (match.kind === 'unique') {
      customerId = match.customerId;
    } else if (match.kind === 'ambiguous') {
      const names = match.candidates.slice(0, 3).map((entry) => entry.number).join(', ');
      issues.push(warning(
        'ALT_NAME',
        `"${altName}" passt auf ${match.candidates.length} Kunden (${names}${match.candidates.length > 3 ? ', …' : ''}) – bitte auswählen. Wird sonst ohne Kunde angelegt.`,
      ));
    } else if (match.kind === 'none') {
      issues.push(warning('ALT_NAME', `Kein Kunde namens "${altName}" gefunden – wird ohne Kunde angelegt.`));
    }

    const notes = buildMachineNotes(values);
    const blocked = issues.some((issue) => issue.level === 'error');
    const draft: MachineDraft | null = blocked || !typeId ? null : {
      number,
      typeId,
      customerId,
      manufacturer: manufacturer ?? '',
      model: model ?? '',
      activeSince: purchase.iso,
      notes,
    };

    rows.push({
      line: row.line,
      number,
      label: [manufacturer, model].filter(Boolean).join(' ') || `Zeile ${row.line}`,
      action: blocked ? 'blocked' : existingNumbers.has(number) ? 'update' : 'create',
      draft,
      issues,
    });
  }

  return {
    rows,
    counts: countActions(rows),
    blankRowsSkipped: table.blankRowsSkipped,
    unusedColumns,
    missingColumns,
  };
}

/**
 * LIEFERANT ist eine Kreditorennummer -- die Lieferantendatei haben wir (noch) nicht, also kann
 * daraus kein Bezug werden. Wörtlich in die Notiz: nichts verloren, nichts erfunden.
 * ALT_NAME kommt mit, weil er der einzige Beleg dafür ist, WARUM ein Kunde zugeordnet wurde.
 */
function buildMachineNotes(values: Readonly<Record<string, string>>): string | null {
  const lines: string[] = [];
  const supplier = clean(values.LIEFERANT ?? '');
  const altName = clean(values.ALT_NAME ?? '');
  const rawType = clean(values.GERATE_ART ?? '');
  if (supplier) lines.push(`LIEFERANT: ${supplier}`);
  if (altName) lines.push(`ALT_NAME: ${altName}`);
  if (rawType) lines.push(`GERATE_ART: ${rawType}`);
  if (lines.length === 0) return null;
  return ['Aus C.O.S. Arista übernommen:', ...lines].join('\n');
}

/**
 * Die GERATE_ART-Werte der Datei mit Häufigkeit -- Grundlage für die Zuordnungstabelle.
 * Nach Häufigkeit sortiert: die ersten zehn decken 86 % der Maschinen ab, danach wird es zäh.
 */
export interface MachineTypeValue {
  readonly value: string;
  readonly count: number;
  readonly samples: readonly string[];
}

export function collectMachineTypeValues(table: CsvTable, sampleSize = 2): readonly MachineTypeValue[] {
  const grouped = new Map<string, { count: number; samples: string[] }>();
  for (const row of table.rows) {
    const values = rowValues(table.header, row);
    const value = (values.GERATE_ART ?? '').trim();
    if (!value) continue;
    const entry = grouped.get(value) ?? { count: 0, samples: [] };
    entry.count += 1;
    const sample = [clean(values.FABRIKAT ?? ''), clean(values.GERATE_TYP ?? '')].filter(Boolean).join(' ');
    if (sample && entry.samples.length < sampleSize) entry.samples.push(sample);
    grouped.set(value, entry);
  }
  return [...grouped.entries()]
    .map(([value, entry]) => ({ value, count: entry.count, samples: entry.samples }))
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value, 'de'));
}

/**
 * Schlägt Zuordnungen vor, wo der Name EXAKT auf einen FORQ-Typ passt -- als Vorbelegung der
 * Tabelle, die der Anwender bestätigt.
 *
 * Bewusst nur exakte Treffer (Groß/klein und Leerzeichen egal, sonst nichts). Kein Fuzzy-Matching:
 * "Elektro Gabelstapler" trifft NICHT den Typ "Gabelstapler", denn ob der Antrieb Teil des Typs
 * oder ein technisches Merkmal ist, ist eine fachliche Entscheidung und keine Rechenaufgabe --
 * und die trifft ihr, nicht ich. Gemessen am echten Export deckt der exakte Abgleich die großen
 * Brocken ab (Niederhubwagen 512, Hochhubwagen 470, Gabelstapler 261 …); der lange Schwanz aus
 * Tippfehlern und Einzelstücken gehört ohnehin vor Augen.
 */
export function suggestTypeMapping(
  values: readonly MachineTypeValue[],
  types: readonly MachineTypeOption[],
): Readonly<Record<string, string>> {
  const byKey = new Map<string, Set<string>>();
  for (const type of types) {
    for (const key of typeKeys(type.name)) {
      const bucket = byKey.get(key) ?? new Set<string>();
      bucket.add(type.id);
      byKey.set(key, bucket);
    }
  }

  const mapping: Record<string, string> = {};
  for (const entry of values) {
    const hits = new Set<string>();
    for (const key of typeKeys(entry.value)) {
      for (const id of byKey.get(key) ?? []) hits.add(id);
    }
    // Nur bei GENAU einem Treffer. Heißen zwei Typen gleich, entscheidet der Anwender.
    if (hits.size === 1) mapping[entry.value] = [...hits][0];
  }
  return mapping;
}

/**
 * Vergleichsschlüssel eines Typnamens: der Name selbst und seine Ein-/Mehrzahl-Form.
 *
 * Ein/Mehrzahl abzufangen ist keine fachliche Entscheidung, sondern Grammatik: "Scheuersaugmaschine"
 * und "Scheuersaugmaschinen" sind dasselbe Gerät, da gibt es nichts zu raten. Genau dieser Fall
 * ist echt -- FORQ führt den Typ einzahlig, Arista schreibt ihn mehrzahlig (72 Maschinen).
 *
 * Bewusst NICHT mehr als das: "Elektro Gabelstapler" bleibt offen, denn ob der Antrieb zum Typ
 * gehört oder ein Merkmal ist, ist eine fachliche Frage -- die beantwortet Fördertechnik Werl.
 */
function typeKeys(value: string): readonly string[] {
  const base = value.toLowerCase().trim().replace(/\s+/g, ' ');
  if (!base) return [];
  return base.endsWith('n') ? [base, base.slice(0, -1)] : [base, `${base}n`];
}
